import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChartColumn } from 'lucide-react';
import z from 'zod';

import { TLAI_FEATURE_FLAGS } from '@/constants';
import { AdminConsoleTabContent } from '@/features/data-harmonization/admin-console/AdminConsoleTabContent';
import { LANDING_TABS_CONFIG } from '@/features/data-harmonization/config/landing-tabs.config';
import { DHUniverseTabContent } from '@/features/data-harmonization/coverage/DHUniverseTabContent';
import { DHTabsLayout } from '@/features/data-harmonization/DHTabsLayout';
import { useTabsConfig } from '@/features/data-harmonization/hooks/useTabsConfig';
import { DashboardsTabContent } from '@/features/data-harmonization/landingPage/DashboardsTabContent';
import { LibrariesTabContent } from '@/features/data-harmonization/landingPage/LibrariesTabContent';
import { TLAIDataHarmonizationLandingTabType } from '@/features/data-harmonization/types';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { PlainLayout } from '@/modus/PlainLayout';
import { FeatureHeader } from '@/shared/FeatureHeader';
import { TLAIDataCheckExecutionStatus } from '@/types/api';

export const Route = createFileRoute('/_protected/t/$tenantId/data-harmonization/')({
  component: DataHarmonizationRoute,
  validateSearch: z.object({
    tab: z.nativeEnum(TLAIDataHarmonizationLandingTabType).optional().catch(undefined),
    page: z.number().optional().catch(undefined),
    pageSize: z.number().optional().catch(undefined),
    dashboard: z.array(z.string()).or(z.string()).optional(),
    status: z
      .array(z.nativeEnum(TLAIDataCheckExecutionStatus))
      .or(z.nativeEnum(TLAIDataCheckExecutionStatus))
      .optional()
      .catch(undefined),
  }),
});

function DataHarmonizationRoute() {
  const navigate = useNavigate();
  const { tenantId } = Route.useParams();
  const { tab: tabParam } = Route.useSearch();
  // The new navigation's sidebar owns tab switching for this landing page.
  const newNavigationEnabled = useFeatureFlag(TLAI_FEATURE_FLAGS.NEW_NAVIGATION, false);

  const { enabledTabs, hasTabAccess, getActiveTab } = useTabsConfig({
    config: LANDING_TABS_CONFIG,
    defaultTab: TLAIDataHarmonizationLandingTabType.DASHBOARDS,
    dashboardId: 'any',
  });

  const activeTab = getActiveTab(tabParam ?? null);

  const handleTabChange = (value: TLAIDataHarmonizationLandingTabType) => {
    navigate({
      to: '/t/$tenantId/data-harmonization',
      params: { tenantId },
      search: { tab: value },
    });
  };

  return (
    <PlainLayout className="flex size-full flex-col gap-6 overflow-y-auto p-1">
      <div className="mx-auto w-full p-4 flex flex-col gap-3.5">
        <FeatureHeader title="Data Harmonization" Icon={ChartColumn}>
          A new approach to data checks, powered by a rebuilt data layer—smarter quality scores, granular error
          diagnostics, and configurable dashboards
        </FeatureHeader>
        <DHTabsLayout
          activeTab={activeTab}
          onTabChange={handleTabChange}
          enabledTabs={enabledTabs}
          hasTabAccess={hasTabAccess}
          listClassName="mb-4"
          hideTabList={newNavigationEnabled}
        >
          <DHTabsLayout.Content value={TLAIDataHarmonizationLandingTabType.DASHBOARDS}>
            <DashboardsTabContent />
          </DHTabsLayout.Content>
          <DHTabsLayout.Content value={TLAIDataHarmonizationLandingTabType.LIBRARIES}>
            <LibrariesTabContent />
          </DHTabsLayout.Content>
          <DHTabsLayout.Content value={TLAIDataHarmonizationLandingTabType.UNIVERSE}>
            <DHUniverseTabContent />
          </DHTabsLayout.Content>
          <DHTabsLayout.Content value={TLAIDataHarmonizationLandingTabType.ADMIN_CONSOLE}>
            <AdminConsoleTabContent />
          </DHTabsLayout.Content>
        </DHTabsLayout>
      </div>
    </PlainLayout>
  );
}
